import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { ITableFiltersProps, IFilterModel, ISortModel, IColumnConfig } from '@/types/admin/dataTable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SearchBar } from './SearchBar';

export const TableFilters = <T,>({
  columns,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  headerRight,
  enableRowSelection = false,
  selectedRows = [],
  totalRows = 0,
  onSelectAll,
  onDeselectAll,
}: ITableFiltersProps<T>) => {
  const styles = useStyles();
  const { admin } = useAdminColors();
  const [showFilters, setShowFilters] = useState(false);

  const filterableColumns = columns.filter((col) => col.filterable !== false);
  const sortableColumns = columns.filter((col) => col.sortable !== false);

  const handleFilterToggle = (field: string, value: any, isMultiSelect = false) => {
    const currentItems = filterModel.items.filter((item) => item.field === field);
    const currentValues = currentItems.map((item) => item.value);

    let newValues: any[];

    if (isMultiSelect) {
      if (currentValues.includes(value)) {
        newValues = currentValues.filter((v) => v !== value);
      } else {
        newValues = [...currentValues, value];
      }
    } else {
      newValues = currentValues.includes(value) ? [] : [value];
    }

    const newFilterModel: IFilterModel = {
      items: [
        ...filterModel.items.filter((item) => item.field !== field),
        ...newValues.map((val) => ({
          field,
          operator: 'is' as const,
          value: val,
        })),
      ],
    };

    onFilterChange(newFilterModel);
  };

  const handleSortChange = (field: string) => {
    const currentSort = sortModel[0];
    let newSort: ISortModel;

    if (currentSort?.field === field) {
      newSort = {
        field,
        sort: currentSort.sort === 'asc' ? 'desc' : 'asc',
      };
    } else {
      newSort = { field, sort: 'asc' };
    }

    onSortChange([newSort]);
  };

  const getSortIcon = (field: string): string => {
    const currentSort = sortModel[0];
    if (currentSort?.field === field) {
      return currentSort.sort === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  const getSelectedValues = (field: string): any[] =>
    filterModel.items.filter((item) => item.field === field).map((item) => item.value);

  const renderFilterSection = (column: IColumnConfig<T>) => {
    const selectedValues = getSelectedValues(String(column.key));

    if (column.filterType === 'select' || column.filterType === 'multiSelect') {
      const isMultiSelect = column.filterType === 'multiSelect';

      return (
        <View key={String(column.key)} style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>{column.label}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.filterChipsContainer}>
              {column.filterOptions?.map((option) => {
                const selected = selectedValues.includes(option.value);
                return (
                  <TouchableOpacity
                    key={String(option.value)}
                    style={[styles.filterChip, selected && styles.filterChipSelected]}
                    onPress={() => handleFilterToggle(String(column.key), option.value, isMultiSelect)}
                  >
                    <Text style={[styles.filterChipText, selected && styles.filterChipTextSelected]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
        </View>
      );
    }

    return null;
  };

  return (
    <View style={styles.container}>
      {showSearch && onSearchChange && (
        <SearchBar
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChangeText={onSearchChange}
          style={styles.searchBar}
        />
      )}

      <View style={styles.filterToggleRow}>
        <TouchableOpacity style={styles.filterToggle} onPress={() => setShowFilters(!showFilters)}>
          <View style={styles.filterToggleContent}>
            <Text style={styles.filterToggleText}>Filters</Text>
            <SVG_COMPONENTS.FILTER width={16} height={16} color={admin.dataTable.text} />
          </View>
        </TouchableOpacity>

        <View style={styles.rightButtons}>
          {headerRight && <View style={styles.headerRightContainer}>{headerRight}</View>}

          {enableRowSelection && totalRows > 0 && (
            <TouchableOpacity
              style={styles.selectAllToggle}
              onPress={() => {
                const allSelected = selectedRows.length === totalRows;
                if (allSelected) {
                  onDeselectAll?.();
                } else {
                  onSelectAll?.();
                }
              }}
            >
              <View style={styles.selectAllToggleContent}>
                <View
                  style={[
                    styles.selectAllIndicator,
                    selectedRows.length === totalRows && styles.selectAllIndicatorSelected,
                    selectedRows.length > 0 &&
                      selectedRows.length < totalRows &&
                      styles.selectAllIndicatorIndeterminate,
                  ]}
                >
                  {selectedRows.length === totalRows && (
                    <SVG_COMPONENTS.CHECK_CIRCLE width={10} height={10} color="#FFFFFF" />
                  )}
                  {selectedRows.length > 0 && selectedRows.length < totalRows && (
                    <View style={styles.indeterminateBar} />
                  )}
                </View>
                <Text style={styles.selectAllText}>
                  {selectedRows.length === totalRows ? 'Deselect All' : 'Select All'}
                </Text>
                {selectedRows.length > 0 && (
                  <Text style={styles.selectionCount}>({selectedRows.length})</Text>
                )}
              </View>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {showFilters && (
        <View style={styles.filtersPanel}>
          {filterableColumns.map((column) => renderFilterSection(column))}

          {sortableColumns.length > 0 && (
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterChipsContainer}>
                  {sortableColumns.map((column) => {
                    const selected = sortModel[0]?.field === String(column.key);
                    return (
                      <TouchableOpacity
                        key={String(column.key)}
                        style={[styles.sortChip, selected && styles.sortChipSelected]}
                        onPress={() => handleSortChange(String(column.key))}
                      >
                        <Text style={[styles.sortChipText, selected && styles.sortChipTextSelected]}>
                          {column.label} {getSortIcon(String(column.key))}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        backgroundColor: 'transparent',
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
        width: '100%',
        zIndex: 1,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
      },
      searchBar: {
        marginHorizontal: 16,
        marginVertical: 12,
      },
      filterToggle: {
        paddingHorizontal: 16,
        paddingVertical: 8,
      },
      filterToggleRow: {
        paddingHorizontal: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      rightButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
      },
      filterToggleContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      },
      filterToggleText: {
        fontSize: 14,
        color: admin.dataTable.text,
        fontWeight: 'bold',
      },
      headerRightContainer: {
        paddingRight: 16,
        paddingBottom: 8,
      },
      filtersPanel: {
        paddingHorizontal: 16,
        paddingTop: 10,
        backgroundColor: 'transparent',
      },
      filterSection: {
        marginBottom: 16,
      },
      filterSectionTitle: {
        fontSize: 12,
        fontWeight: '600',
        color: colors.text.dim,
        marginBottom: 8,
        textTransform: 'uppercase',
      },
      filterChipsContainer: {
        flexDirection: 'row',
        gap: 8,
      },
      filterChip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.filterBg,
      },
      filterChipSelected: {
        backgroundColor: admin.selector.itemSelectedBg,
        borderColor: admin.formInput.borderFocused,
      },
      filterChipText: {
        fontSize: 12,
        color: admin.dataTable.text,
      },
      filterChipTextSelected: {
        color: admin.formInput.borderFocused,
        fontWeight: '600',
      },
      sortChip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.filterBg,
      },
      sortChipSelected: {
        backgroundColor: admin.selector.itemSelectedBg,
        borderColor: admin.formInput.borderFocused,
      },
      sortChipText: {
        fontSize: 12,
        color: admin.dataTable.text,
      },
      sortChipTextSelected: {
        color: admin.formInput.borderFocused,
        fontWeight: '600',
      },
      selectAllToggle: {
        paddingHorizontal: 12,
        paddingVertical: 8,
      },
      selectAllToggleContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      },
      selectAllIndicator: {
        width: 16,
        height: 16,
        borderRadius: 3,
        borderWidth: 1.5,
        borderColor: admin.dataTable.border,
        backgroundColor: 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
      },
      selectAllIndicatorSelected: {
        backgroundColor: admin.formInput.borderFocused,
        borderColor: admin.formInput.borderFocused,
      },
      selectAllIndicatorIndeterminate: {
        backgroundColor: admin.formInput.borderFocused,
        borderColor: admin.formInput.borderFocused,
      },
      indeterminateBar: {
        width: 6,
        height: 1.5,
        backgroundColor: '#FFFFFF',
      },
      selectAllText: {
        fontSize: 12,
        fontWeight: '600',
        color: admin.dataTable.text,
      },
      selectionCount: {
        fontSize: 11,
        color: colors.text.dim,
      },
    });
  });
